import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { authClient, fetchMe } from "../auth";
import { friendlyAdminError } from "../apiError";
import { useT } from "../i18n";
import { HeyLifeTile } from "../components/Brand";
import { Msg } from "../ui";

const DEMO_PASSWORDS = import.meta.env.DEV || import.meta.env.VITE_ALLOW_PASSWORD_LOGIN === "true";

export function AdminLogin() {
  const { t } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "error" | "success"; text: string } | null>(null);

  async function afterSignIn() {
    // Load the fresh session into the cache before entering the shell: invalidating alone leaves a
    // cached `null` (from the redirect that brought us here) in place, and the shell would bounce back.
    await qc.fetchQuery({ queryKey: ["me"], queryFn: fetchMe, staleTime: 0 });
    await navigate({ to: "/admin" });
  }

  async function sendMagicLink(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const res = await authClient.signIn.magicLink({ email, callbackURL: `${window.location.origin}/admin` });
      setMsg(res.error ? { kind: "error", text: friendlyAdminError(t, res.error) } : { kind: "success", text: t("admin.login.sent") });
    } catch {
      setMsg({ kind: "error", text: t("admin.login.apiUnreachable") });
    } finally {
      setBusy(false);
    }
  }

  async function signInPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const res = await authClient.signIn.email({ email, password });
      if (res.error) {
        setMsg({ kind: "error", text: friendlyAdminError(t, res.error) });
        return;
      }
      await afterSignIn();
    } catch {
      setMsg({ kind: "error", text: t("admin.login.apiUnreachable") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="card">
        <span className="brand">
          <HeyLifeTile size={40} />
          Hey Life
        </span>
        <h1>{t("admin.login.title")}</h1>
        <p className="muted">{t("admin.login.subtitle")}</p>

        <form onSubmit={sendMagicLink} style={{ display: "grid", gap: 14 }}>
          <div className="field">
            <label htmlFor="email">{t("admin.login.email")}</label>
            <input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <button className="btn" disabled={busy || !email}>
            {t("admin.login.sendLink")}
          </button>
        </form>
        {DEMO_PASSWORDS ? (
          <form onSubmit={signInPassword} style={{ display: "grid", gap: 14 }}>
            <div className="field">
              <label htmlFor="password">{t("admin.login.password")}</label>
              <input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <button className="btn secondary" disabled={busy || !email || !password}>
              {t("admin.login.signInPassword")}
            </button>
          </form>
        ) : null}
        <Msg msg={msg} />
      </div>
    </div>
  );
}
